import {
  hasOnboardingReactionPlayed,
  isOnboardingActive,
  isOnboardingIntro,
  isOnboardingTour,
  ONBOARDING_ANNOUNCED_DESTINATIONS,
  type OnboardingAnnouncedDestination,
  type OnboardingReaction,
  type OnboardingState,
} from "#onboarding-state";

/**
 * The strip destinations the onboarding hands out. `map` is Learn's own tab presented as the map
 * (Learn's map mode, `/learn/map`): it turns into `learn` once the cards are next.
 */
export type OnboardingDestination = "browse" | "decks" | "learn" | "map" | "search" | "stats";

/**
 * Where the learner is, as the onboarding sees it: Learn is either its map or its cards (every
 * other Learn page); `other` is anywhere it says nothing about.
 */
export type OnboardingPlace = "browse" | "cards" | "decks" | "map" | "other" | "stats";

/** The introductions the sheet plays on a page, the first time the learner opens it. */
export type OnboardingPageReaction = Extract<
  OnboardingReaction,
  "browse" | "decks" | "map" | "stats"
>;

/** What the onboarding's sheet is showing: a page's introduction, or a destination it unlocked. */
export type OnboardingSheetSubject =
  | { destination: OnboardingAnnouncedDestination; kind: "announcement" }
  | { kind: "reaction"; reaction: OnboardingPageReaction };

/** The reaction that means an announced destination has been reached. */
const ANNOUNCED_DESTINATION_ARRIVALS: Record<OnboardingAnnouncedDestination, OnboardingReaction> = {
  browse: "browse",
  decks: "decks",
  learn: "cards",
  stats: "stats",
};

/** Where an announced destination is: Learn's announcement sends the learner to the cards. */
const ANNOUNCED_DESTINATION_PLACES: Record<OnboardingAnnouncedDestination, OnboardingPlace> = {
  browse: "browse",
  decks: "decks",
  learn: "cards",
  stats: "stats",
};

/**
 * Whether a reaction has unlocked `destination`: Browse with the map's introduction, Learn with
 * Browse's (or the cards, or a character learned some other way), Stats with the first character
 * learned, and Deck with Stats' introduction.
 */
export function isOnboardingDestinationUnlocked(
  state: OnboardingState,
  destination: OnboardingAnnouncedDestination,
): boolean {
  const played = (reaction: OnboardingReaction) => hasOnboardingReactionPlayed(state, reaction);
  switch (destination) {
    case "browse": {
      return played("map") || isOnboardingDestinationUnlocked(state, "learn");
    }
    case "learn": {
      return played("browse") || played("cards") || played("first-character");
    }
    case "stats": {
      return played("first-character");
    }
    case "decks": {
      return played("stats");
    }
  }
}

// One array per step, so an unchanged step hands the strip the very same list.
const MAP_ONLY: readonly OnboardingDestination[] = ["map"];
const MAP_AND_BROWSE: readonly OnboardingDestination[] = ["map", "browse"];
const LEARN_AND_BROWSE: readonly OnboardingDestination[] = ["learn", "browse"];
const STATS_LEARN_AND_BROWSE: readonly OnboardingDestination[] = ["stats", "learn", "browse"];
const STUDY_DESTINATIONS: readonly OnboardingDestination[] = ["stats", "decks", "learn", "browse"];

/**
 * Which destinations the strip offers, or `null` once the onboarding no longer limits it: the map
 * alone, then Browse beside it, then Learn in the map's place, then Stats with the first learned
 * character, then Deck once Stats has been introduced, and finally everything (Search arrives
 * with the first level).
 */
export function getOnboardingUnlockedDestinations(
  state: OnboardingState | null,
): readonly OnboardingDestination[] | null {
  if (!isOnboardingActive(state)) {
    return null;
  }
  if (!isOnboardingDestinationUnlocked(state, "learn")) {
    return isOnboardingDestinationUnlocked(state, "browse") ? MAP_AND_BROWSE : MAP_ONLY;
  }
  if (!isOnboardingDestinationUnlocked(state, "stats")) {
    return LEARN_AND_BROWSE;
  }
  return isOnboardingDestinationUnlocked(state, "decks")
    ? STUDY_DESTINATIONS
    : STATS_LEARN_AND_BROWSE;
}

/**
 * The introduction the sheet plays at `place`, if the learner has opened it for the first time
 * (or walked away from its introduction before): the map's while the tour is on it, and each
 * unlocked destination's own.
 */
export function getOnboardingPageReaction(
  state: OnboardingState | null,
  place: OnboardingPlace,
): OnboardingPageReaction | null {
  if (!isOnboardingActive(state) || isOnboardingIntro(state)) {
    return null;
  }
  const isUnplayed = (reaction: OnboardingPageReaction) =>
    !hasOnboardingReactionPlayed(state, reaction);
  switch (place) {
    case "map": {
      return isOnboardingTour(state) && isUnplayed("map") ? "map" : null;
    }
    case "browse":
    case "decks":
    case "stats": {
      return isOnboardingDestinationUnlocked(state, place) && isUnplayed(place) ? place : null;
    }
    case "cards":
    case "other": {
      return null;
    }
  }
}

/**
 * The unlocked destination the sheet announces at `place`, if any: one the learner has neither
 * reached nor answered (followed, or passed over by switching elsewhere), first in strip order.
 * Never the place the learner is already at.
 */
export function getOnboardingAnnouncement(
  state: OnboardingState | null,
  place: OnboardingPlace,
): OnboardingAnnouncedDestination | null {
  if (!isOnboardingActive(state) || isOnboardingIntro(state)) {
    return null;
  }
  return (
    ONBOARDING_ANNOUNCED_DESTINATIONS.find(
      (destination) =>
        isOnboardingDestinationUnlocked(state, destination) &&
        !hasOnboardingReactionPlayed(state, ANNOUNCED_DESTINATION_ARRIVALS[destination]) &&
        !state.answeredAnnouncements.includes(destination) &&
        ANNOUNCED_DESTINATION_PLACES[destination] !== place,
    ) ?? null
  );
}

/**
 * What the sheet shows at `place`: the page's introduction first, else an announcement, else
 * nothing (the sheet is away).
 */
export function getOnboardingSheetSubject(
  state: OnboardingState | null,
  place: OnboardingPlace,
): OnboardingSheetSubject | null {
  const reaction = getOnboardingPageReaction(state, place);
  if (reaction !== null) {
    return { kind: "reaction", reaction };
  }
  const destination = getOnboardingAnnouncement(state, place);
  return destination === null ? null : { destination, kind: "announcement" };
}

/**
 * The strip a learner part-way through the onboarding sees: their own destinations in their own
 * order, limited to what the onboarding has unlocked, with the map standing in Learn's place
 * while it is unlocked instead. An unlocked destination the stored strip lacks is added after
 * the rest, so the onboarding's next step is always on the strip.
 */
export function applyOnboardingUnlockedDestinations<D extends string>(
  stored: readonly D[],
  unlocked: readonly D[] | null,
): D[] {
  if (unlocked === null) {
    return [...stored];
  }
  const map = unlocked.find((destination) => destination === "map");
  const order = stored.map((destination) =>
    map !== undefined && destination === "learn" ? map : destination,
  );
  const strip = order.filter((destination) => unlocked.includes(destination));
  for (const destination of unlocked) {
    if (!strip.includes(destination)) {
      strip.push(destination);
    }
  }
  return strip;
}
